import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/react-magnetic-di/no-unneeded -- magnetic-di does not work automatically with ternary components. it needs to have the `di`
import { di } from 'react-magnetic-di';
import Tabs, { Tab, TabList } from '@atlaskit/tabs';
import { fg } from '@atlassian/jira-feature-gating';
import { useScreenTabs as useUiModificationsScreenTabs } from '@atlassian/ui-modifications-core/controllers';
import { ConditionallyVisibleDiv } from '@atlassian/ui-modifications-core';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { useIsGICPositioningEnabled } from '../../../../controllers/gic-positioning-enabled/main.tsx';
import FieldRenderer from './field-renderer/index.tsx';
import { FieldsContainer } from './fields-container/index.tsx';
import type { Props } from './types.tsx';

export const LayoutRenderer = (props: Props) => {
	// eslint-disable-next-line jira/react-magnetic-di/no-unneeded -- magnetic-di does not work automatically with ternary components. it needs to have the `di`
	di(FieldRenderer, FieldsContainer);

	const { fieldTabs, fieldConfig, getFormState, setFieldValue, apiLatency, optOutSmartFieldIds } =
		props;
	const isGICPositioningEnabled = useIsGICPositioningEnabled();
	// TODO Add this field with the fields on previous line when cleaning FF
	const fieldPropsExtensions = isGICPositioningEnabled() ? props.fieldPropsExtensions : undefined;
	const [{ currentActiveTabIndex }, { setCurrentActiveTabIndex }] = useFormContext();

	const tabs = useMemo(
		() =>
			fieldTabs.map(({ id, tabId, name, items, collapsedItems }) => ({
				id: tabId || id,
				name,
				content: null,
				testId: id,
				items,
				collapsedItems,
			})),
		[fieldTabs],
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onSelect = (_: any, selectedIndex: number) => {
		setCurrentActiveTabIndex(selectedIndex);
	};

	const handleChange = (index: number) => {
		onSelect(null, index);
	};

	const { hasValidationErrors, submitFailed } = getFormState();
	const { controlledTabs, controlledSelected, controlledOnChange } = useUiModificationsScreenTabs({
		tabs,
		viewProps: { shouldDisableScreenTabsControl: hasValidationErrors && submitFailed },
		selected: currentActiveTabIndex,
		onChange: handleChange,
	});

	if (!fieldTabs.length) {
		return null;
	}

	// TODO When cleaning migrate_gic_smart_fields_jira_labs, move FieldsContainer to inline instead of using ComponentToRender
	const ComponentToRender = fg('migrate_gic_smart_fields_jira_labs')
		? FieldsContainer
		: FieldRenderer;

	if (fieldTabs.length === 1) {
		return (
			<ComponentToRender
				fieldConfig={fieldConfig}
				fields={fieldTabs[0].items}
				{...(fg('migrate_gic_smart_fields_jira_labs') && {
					collapsedFields: fieldTabs[0].collapsedItems,
					FieldRenderer,
					tabIndex: 0,
				})}
				optOutSmartFieldIds={optOutSmartFieldIds}
				apiLatency={apiLatency}
				getFormState={getFormState}
				setFieldValue={setFieldValue}
				fieldPropsExtensions={isGICPositioningEnabled() ? fieldPropsExtensions : undefined}
			/>
		);
	}

	const getTabsList = () => (
		<>
			<Tabs
				testId="issue-create.ui.modal.create-form.layout-renderer.layout-rerender-tabs"
				selected={controlledSelected}
				onChange={controlledOnChange}
				id="issue-create-modal-tabs"
				shouldUnmountTabPanelOnChange
			>
				<TabList>
					{controlledTabs.map((tab) => (
						<ConditionallyVisibleDiv key={tab.id} isVisible={tab.isVisible}>
							<Tab testId={tab.testId} key={tab.id}>
								{tab.name}
							</Tab>
						</ConditionallyVisibleDiv>
					))}
				</TabList>
			</Tabs>
			{controlledTabs.map(({ id, items, collapsedItems, testId }, index) => (
				<ConditionallyVisibleDiv
					id={`issue-create-modal-tabs-${index}-tab`}
					role="tabpanel"
					key={id}
					isVisible={index === controlledSelected}
					testId={`tab-content-${testId}`}
				>
					<ComponentToRender
						fieldConfig={fieldConfig}
						fields={items}
						{...(fg('migrate_gic_smart_fields_jira_labs') && {
							collapsedFields: collapsedItems,
							FieldRenderer,
							tabIndex: index,
						})}
						optOutSmartFieldIds={optOutSmartFieldIds}
						getFormState={getFormState}
						setFieldValue={setFieldValue}
						apiLatency={apiLatency}
						fieldPropsExtensions={isGICPositioningEnabled() ? fieldPropsExtensions : undefined}
					/>
				</ConditionallyVisibleDiv>
			))}
		</>
	);

	return fg('show-all-tabs-in-create-and-transition-screen') ? (
		<TabsWrapper>{getTabsList()}</TabsWrapper>
	) : (
		getTabsList()
	);
};

export default LayoutRenderer;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TabsWrapper = styled.div({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"div[role='tablist']": {
		flexWrap: 'wrap',
	},
});
